// frontend/src/shared/components/EntityPicker.tsx
// ADR-089 GES-05: جایگزین ورودی دستی UUID در FormFillPage
import { useEffect, useRef, useState } from "react";
import { axiosInstance } from "@/api/client"; // همان instance مشترک پروژه با JWT interceptor

interface EntityResult {
  id: string;
  display: string;
  model: string;
}

interface Props {
  model: string; // "inventory.materialrevision"
  value: { id: string; display: string } | null;
  onChange: (value: { id: string; display: string } | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function EntityPicker({ model, value, onChange, disabled, placeholder }: Props) {
  const [query, setQuery] = useState(value?.display || "");
  const [results, setResults] = useState<EntityResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value?.display || "");
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const performSearch = (text: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const res = await axiosInstance.get("/api/v1/shared/entity-search/", {
          params: { model, q: text },
        });
        setResults(res.data.results || []);
        setIsOpen(true);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, text.trim() ? 400 : 0);
  };

  const handleInputChange = (text: string) => {
    setQuery(text);
    onChange(null);
    performSearch(text);
  };

  const handleFocus = () => {
    if (!isOpen) {
      setIsOpen(true);
      performSearch(query);
    }
  };

  const handleSelect = (item: EntityResult) => {
    onChange({ id: item.id, display: item.display });
    setQuery(item.display);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        className="input-field"
        disabled={disabled}
        placeholder={placeholder || "جست‌وجو..."}
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleFocus}
      />
      {isLoading && (
        <span style={{ position: "absolute", left: "0.75rem", top: "0.6rem", fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
          در حال جست‌وجو...
        </span>
      )}
      {isOpen && results.length > 0 && (
        <div
          className="brand-card"
          style={{
            position: "absolute", top: "100%", right: 0, left: 0, zIndex: 20,
            marginTop: "0.25rem", maxHeight: "220px", overflowY: "auto", padding: "0.25rem",
          }}
        >
          {results.map((item) => (
            <div
              key={item.id}
              onClick={() => handleSelect(item)}
              style={{ padding: "0.5rem 0.75rem", cursor: "pointer", borderRadius: "6px" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {item.display}
            </div>
          ))}
        </div>
      )}
      {isOpen && !isLoading && results.length === 0 && (
        <div className="brand-card" style={{ position: "absolute", top: "100%", right: 0, left: 0, zIndex: 20, marginTop: "0.25rem", padding: "0.75rem" }}>
          <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>موردی یافت نشد.</span>
        </div>
      )}
    </div>
  );
}
